import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef } from 'react';

import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { ElementAddress, MediaText } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

const DEBOUNCE_TIME = 1000;

/**
 * For protected custom fonts: request server-side SVG path generation.
 * Sends fonts.renderTextRequested to host, host calls /v1/fonts/render,
 * host sends back fonts.renderTextResult with SVG path string.
 *
 * The path is stored on the element so FabricPathText renders from it.
 */
function useProtectedFontRender(element: MediaText, elementAddress: ElementAddress, disabled = false): void {
  const dispatch = useDispatch();
  const uploadedFonts = useSelector((state) => state.fonts.uploadedFonts);
  const fontFile = element.extra?.fontFamily;
  const uploadedFont = uploadedFonts.find((f) => f.fontFile === fontFile);
  const isProtected = uploadedFont?.isProtected === true;

  const lastRequestRef = useRef<string>('');

  // Listen for render results from host — store path on element
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!disabled && e.data?.type === 'fonts.renderTextResult' && e.data?.data?.elementUuid === element.uuid) {
        const { path } = e.data.data;
        if (path) {
          dispatch(
            updateMediaElementOperation(elementAddress, { path } as Partial<MediaText>, MediaUpdateActionName.MOVED),
          );
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [disabled, element.uuid, elementAddress, dispatch]);

  const debouncedRequest = useCallback(
    debounce(
      (data: { fontFile: string; textElement: MediaText; elementUuid: number }) => {
        sendPostMessage('fonts.renderTextRequested', data);
      },
      DEBOUNCE_TIME,
      { leading: true, trailing: true },
    ),
    [element.uuid],
  );

  // Re-request render when any visual property changes (same fields as getDigitizeTextId hash)
  useEffect(() => {
    if (disabled) {
      debouncedRequest.cancel();
      lastRequestRef.current = '';
      return;
    }

    if (!isProtected || !fontFile) {
      lastRequestRef.current = '';
      return;
    }

    const cacheKey = [
      fontFile,
      element.width,
      element.height,
      element.fill,
      element.extra?.text,
      element.extra?.fontSize,
      element.extra?.textAlign ?? '',
      element.extra?.lineHeight ?? '',
      element.extra?.underline ? '1' : '0',
      element.extra?.linethrough ? '1' : '0',
      element.extra?.direction ?? '',
      element.extra?.letterSpacing ?? '',
      element.extra?.paragraphSpacing ?? '',
      element.extra?.sectionSpacing ?? '',
      element.extra?.stroke ? `${element.extra.stroke.color}${element.extra.stroke.width}` : '',
      element.extra?.shadow
        ? `${element.extra.shadow.color}${element.extra.shadow.offsetX}${element.extra.shadow.offsetY}${element.extra.shadow.blur}`
        : '',
    ].join('|');

    if (cacheKey === lastRequestRef.current) {
      return;
    }
    lastRequestRef.current = cacheKey;

    const textContent = element.extra?.text;
    if (!textContent || !textContent.trim()) {
      lastRequestRef.current = '';
      if (element.path) {
        dispatch(
          updateMediaElementOperation(elementAddress, { path: '' } as Partial<MediaText>, MediaUpdateActionName.MOVED),
        );
      }
      return;
    }

    debouncedRequest({
      fontFile,
      textElement: element,
      elementUuid: element.uuid,
    });
  }, [
    isProtected,
    fontFile,
    element.width,
    element.height,
    element.fill,
    element.extra?.text,
    element.extra?.fontSize,
    element.extra?.textAlign,
    element.extra?.lineHeight,
    element.extra?.underline,
    element.extra?.linethrough,
    element.extra?.direction,
    element.extra?.letterSpacing,
    element.extra?.paragraphSpacing,
    element.extra?.sectionSpacing,
    element.extra?.stroke,
    element.extra?.shadow,
    element.uuid,
    debouncedRequest,
    disabled,
  ]);
}

export default useProtectedFontRender;
